/**
 * 島根県の背景ツアー「島根めぐり」コンテンツ。
 *
 * 軸は一日の時間。夜明けの稲佐の浜から、日没後の日御碕灯台へ。
 *
 * 鳥居について。
 *  稲佐の浜の弁天島と、宍道湖の嫁ヶ島には鳥居がある。
 *  だから両方とも「鳥居を描かない」という否定文を入れていない。
 *  出雲大社も同じ理由で入れていない。
 *
 * 足立美術館について。
 *  アメリカの専門誌のランキングは民間の評価なので出典に使わない。
 *  庭そのものの事実（枯山水庭・白砂青松庭・借景）だけを書く。
 *  所蔵作品の図像は著作権があるので描かせない。庭だけを描く。
 *
 * 三瓶山について。
 *  気象庁の活火山。中国地方の活火山は三瓶山と阿武火山群の2つだけ。
 *  ただし噴火警戒レベルの数字は変わるので書かない。
 *
 * 調査で出てきた「書いてはいけないこと」。
 *  - 出雲大社の「日本最古の神社」「大社造は日本最古の様式」はどちらも断定形の公的記述がない。
 *  - 大注連縄の「日本一」も使わない。長さ約13メートル、重さ5.2トンという数値だけを書く。
 *  - 御本殿の高さが「かつて48メートル」等は社伝・伝聞形。書かない。
 *    宇豆柱の発見（平成12年・直径3メートル）は事実として書ける。
 *  - 日御碕灯台の「石造で日本一の高さ」は国・県の断定が取れない。数値だけを書く。
 *  - 摩天崖は環境省の表現どおり「日本屈指の高さ」。「日本一」に言い換えない。
 *  - 松江城天守の「唯一の正統天守」は公式自身が伝聞形。使わない。
 *  - 龍源寺間歩の入場料・開館時間は変わる。「有料・時間制」とだけ書く。
 *  - 神在祭の日付は旧暦なので新暦で固定して書かない。
 *  - 小泉八雲の著作の引用、映像作品との結びつけはしない。
 */

import type { StageTour } from '../types';

export const shimaneSceneryTour: StageTour = {
  scenes: [
    {
      id: 'inasa-no-hama',
      name: '稲佐の浜',
      area: '出雲市・夜明け',
      image: '/assets/scenery/shimane/scenes/01_inasa_no_hama.webp',
      hotspots: [
        {
          id: 'inasa-kuniyuzuri',
          x: 0.46,
          y: 0.44,
          title: '国譲りが伝わる浜',
          body: '浜の近くに、大国主大神と建御雷之男神が国譲りの交渉を行ったと伝わる屏風岩があります。出雲大社の西方およそ1キロ、日本海に面した長い砂浜です。',
          source: 'https://www.izumo-kankou.gr.jp/spot/sightseeing/213',
          mapQuery: '稲佐の浜 出雲市',
        },
        {
          id: 'inasa-bentenjima',
          x: 0.72,
          y: 0.38,
          title: '砂浜が広がり続けた',
          body: '浜に立つ小さな岩の島は地元で「べんてんさん」と呼ばれます。かつては稲佐湾のはるか沖にあったため沖ノ御前、沖ノ島とも呼ばれていました。昭和60年前後までは島の目前まで波が打ち寄せていましたが、近年は砂浜が広がり、今は島の裏側まで歩いて行けます。',
          source: 'https://www.izumo-kankou.gr.jp/spot/sightseeing/213',
          mapQuery: '弁天島 稲佐の浜',
        },
        {
          id: 'inasa-kamimukae',
          x: 0.22,
          y: 0.48,
          title: '旧暦十月十日の神迎神事',
          body: '旧暦10月10日、全国の八百万の神々を迎える神迎神事がこの浜で行われます。夜、浜に火が焚かれ、神事が終わると提灯と楽の音に導かれて出雲大社まで行列が進みます。日付は旧暦なので、出かける前に最新の発表を確かめてください。',
          source: 'https://www.izumo-kankou.gr.jp/6404',
          mapQuery: '稲佐の浜 神迎神事',
        },
      ],
    },
    {
      id: 'izumo-taisha',
      name: '出雲大社',
      area: '出雲市・朝',
      image: '/assets/scenery/shimane/scenes/02_izumo_taisha.webp',
      hotspots: [
        {
          id: 'izumo-kokuho',
          x: 0.46,
          y: 0.4,
          title: '御本殿は国宝',
          body: '御本殿は大社造りと呼ばれる建築様式で建てられており、国宝に指定されています。平成12年には、境内で直径3メートルもある巨大な柱根「宇豆柱」が発見されました。',
          source: 'https://www.kankou-shimane.com/spot/449.html',
          mapQuery: '出雲大社 御本殿',
        },
        {
          id: 'izumo-shimenawa',
          x: 0.74,
          y: 0.5,
          title: '長さ13メートルの大注連縄',
          body: '神楽殿正面の大注連縄は長さおよそ13メートル、重さ5.2トン。数年に一度、新しい注連縄へ懸け替えられます。神楽殿の大広間は270畳の広さがあります。',
          source: 'https://izumooyashiro.or.jp/keidai/kaguraden',
          mapQuery: '出雲大社 神楽殿',
        },
        {
          id: 'izumo-kamiarizuki',
          x: 0.22,
          y: 0.48,
          title: '出雲だけが神在月',
          body: '旧暦10月、日本各地の神々が出雲大社に集まって神議りを行うとされます。そのため全国では神様が留守になる神無月と呼ぶのに対し、出雲では神在月と呼びます。',
          source: 'https://www.izumo-kankou.gr.jp/6404',
          mapQuery: '出雲大社 神在祭',
        },
      ],
    },
    {
      id: 'omori',
      name: '石見銀山 大森の町並み',
      area: '大田市・午前',
      image: '/assets/scenery/shimane/scenes/03_omori.webp',
      hotspots: [
        {
          id: 'omori-sekaiisan',
          x: 0.46,
          y: 0.42,
          title: '2007年7月に世界遺産へ',
          body: '石見銀山遺跡とその文化的景観は、2007年7月に世界遺産に登録されました。周辺の環境に配慮した自然環境と共存する産業遺跡である点が評価されています。',
          source: 'https://www.kankou-shimane.com/spot/452.html',
          mapQuery: '石見銀山世界遺産センター',
        },
        {
          id: 'omori-judenken',
          x: 0.74,
          y: 0.36,
          title: '国が選んだ鉱山町',
          body: '大田市大森銀山は、文化庁の重要伝統的建造物群保存地区一覧に鉱山町として載っています。選定は昭和62年12月5日、面積は162.7ヘクタール。島根県内では大森銀山のほか、温泉津、津和野、美保関が選ばれています。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '大森の町並み 大田市',
        },
        {
          id: 'omori-kumagai',
          x: 0.22,
          y: 0.5,
          title: '豪商の家が残る町並み',
          body: '大森の町並みには、江戸時代の武家屋敷や代官所跡、石見銀山で栄えた豪商の熊谷家住宅など、歴史的な建造物や文化財が並び、当時の面影を残しています。熊谷家は石見銀山御料内でもっとも有力な商家のひとつで、国の重要文化財に指定されています。',
          source: 'https://www.kankou-shimane.com/spot/452.html',
          mapQuery: '熊谷家住宅 大田市',
        },
      ],
    },
    {
      id: 'ryugenji-mabu',
      name: '龍源寺間歩',
      area: '大田市・昼前',
      image: '/assets/scenery/shimane/scenes/04_ryugenji_mabu.webp',
      hotspots: [
        {
          id: 'ryugenji-600m',
          x: 0.46,
          y: 0.44,
          title: '1715年に開かれた600メートルの坑道',
          body: '龍源寺間歩は1715年に開発された坑道で、大久保間歩に次いで長く600メートルあります。石見銀山の数多い間歩のなかで、常時一般公開されている唯一の間歩です。',
          source: 'https://www.kankou-shimane.com/spot/452.html',
          mapQuery: '龍源寺間歩',
        },
        {
          id: 'ryugenji-kekuro',
          x: 0.72,
          y: 0.34,
          title: '坑道は狭く、壁は結露している',
          body: '大田市は見学者に、坑道は狭く、結露した壁ははがれやすいので頭上や壁に十分注意して進むよう案内しています。有料で時間の決まった見学施設なので、出かける前に最新の発表を確かめてください。',
          source: 'https://www.city.oda.lg.jp/ohda_city/city_organization/23/596/15162/3672',
          mapQuery: '龍源寺間歩 大田市',
        },
        {
          id: 'ryugenji-kuruma',
          x: 0.22,
          y: 0.48,
          title: '銀山地区へは車で入らない',
          body: '自家用車は石見銀山世界遺産センターの駐車場に停め、そこから路線バスで移動するパーク＆ライド方式がとられています。大森地区と銀山地区は道幅が狭く十分な駐車スペースが確保できないため、歩行者の安全のためにも季節を問わず交通規制が行われています。',
          source: 'https://www.ginzan-wm.jp/traffic_info/?tab=cat02',
          mapQuery: '石見銀山世界遺産センター 駐車場',
        },
      ],
    },
    {
      id: 'matsue-jo',
      name: '松江城',
      area: '松江市・昼',
      image: '/assets/scenery/shimane/scenes/05_matsue_jo.webp',
      hotspots: [
        {
          id: 'matsue-kokuho',
          x: 0.46,
          y: 0.36,
          title: '2015年に国宝へ',
          body: '松江城天守は2015年に国宝に指定されました。天守そのものは慶長16年の完成です。',
          source: 'https://www.matsue-castle.jp/highlight/index',
          mapQuery: '松江城 天守',
        },
        {
          id: 'matsue-boro',
          x: 0.74,
          y: 0.5,
          title: '望楼型で、附櫓がつく',
          body: '松江城天守は上に物見の望楼をのせた望楼型で、入口側に附櫓がついた複合式の天守です。現存する12天守のなかで高さは第3位とされています。',
          source: 'https://www.matsue-castle.jp/highlight/index',
          mapQuery: '松江城 附櫓',
        },
        {
          id: 'matsue-12tenshu',
          x: 0.22,
          y: 0.48,
          title: '江戸時代の天守は全国に12しかない',
          body: '松江城天守は、全国に12箇所しか残っていない江戸時代に建設された城郭のひとつです。',
          source: 'https://www.matsue-castle.jp/faq/index',
          mapQuery: '松江城山公園',
        },
      ],
    },
    {
      id: 'kuniga-kaigan',
      name: '隠岐 国賀海岸 摩天崖',
      area: '西ノ島町・昼過ぎ',
      image: '/assets/scenery/shimane/scenes/06_kuniga_kaigan.webp',
      hotspots: [
        {
          id: 'kuniga-matengai',
          x: 0.46,
          y: 0.42,
          title: '257メートルの摩天崖',
          body: '日本海の荒波に削られた国賀海岸では、草原を散策しながら海岸景観を眺められます。257メートルの摩天崖は日本屈指の高さで、遊覧船からも眺められます。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/daisen-oki/spot/',
          mapQuery: '摩天崖 西ノ島町',
        },
        {
          id: 'kuniga-houboku',
          x: 0.74,
          y: 0.52,
          title: '断崖の上で牛や馬が草を食む',
          body: '西ノ島町では、島全体に広がる自然草地や山林を使って伝統的に放牧が続けられており、牛馬がのんびりと草を食む様子をいたるところで見られます。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/daisen-oki/spot/',
          mapQuery: '国賀海岸 西ノ島町',
        },
        {
          id: 'kuniga-geopark',
          x: 0.22,
          y: 0.48,
          title: 'ユネスコ世界ジオパークの島',
          body: '隠岐諸島はユネスコ世界ジオパークに認定されています。隠岐はかつて大陸の一部で、600万年前から40万年前にかけての火山活動で島の姿がつくられ、40万年前以降に本土から離れて離島になりました。',
          source: 'https://www.oki-geopark.jp/geohistory/',
          mapQuery: '隠岐ジオパーク 西ノ島',
        },
      ],
    },
    {
      id: 'adachi-teien',
      name: '足立美術館の庭園',
      area: '安来市・午後',
      image: '/assets/scenery/shimane/scenes/07_adachi_teien.webp',
      hotspots: [
        {
          id: 'adachi-yotsu',
          x: 0.46,
          y: 0.44,
          title: '庭と滝、あわせて五万坪',
          body: '枯山水庭は伝統的な手法で大自然の風趣を表し、白砂青松庭は横山大観の作品をモチーフに創作されました。ほかに杉苔を中心とした京風の苔庭、水面の光と影が静かな池庭、そして亀鶴の滝があり、あわせて五万坪に及びます。',
          source: 'https://www.adachi-museum.or.jp/garden',
          mapQuery: '足立美術館 枯山水庭',
        },
        {
          id: 'adachi-shakkei',
          x: 0.74,
          y: 0.34,
          title: '背後の山まで庭のうち',
          body: '庭園は背後の山々を借景として取り込むように設計されています。公式は、借景の自然の山々との調和はまさに生きた日本画ともいえる、と表現しています。',
          source: 'https://www.adachi-museum.or.jp/garden',
          mapQuery: '足立美術館 安来市',
        },
        {
          id: 'adachi-soritsu',
          x: 0.22,
          y: 0.5,
          title: '71歳で美術館をつくった人',
          body: '創設者の足立全康は、郷土への恩返しと島根県の文化発展の一助になればという思いで、71歳だった1970年に美術館を創設しました。若い頃から何よりも庭造りが好きで、朝に夕に庭を見ては、気に入らないところがあると庭師を呼んで陣頭指揮をとったと紹介されています。',
          source: 'https://www.adachi-museum.or.jp/about',
          mapQuery: '足立美術館',
        },
      ],
    },
    {
      id: 'sanbesan',
      name: '三瓶山',
      area: '大田市・夕方前',
      image: '/assets/scenery/shimane/scenes/08_sanbesan.webp',
      hotspots: [
        {
          id: 'sanbe-muttsu',
          x: 0.46,
          y: 0.4,
          title: '六つの峰が輪になって並ぶ',
          body: '三瓶山は男三瓶山・女三瓶山・子三瓶山・孫三瓶山などの六つの峰が、中央にある室の内を囲むように環状に並んでいます。山頂と山麓の草原、山腹の自然林など、変化のある景観が続きます。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/daisen-oki/spot/',
          mapQuery: '三瓶山 室の内',
        },
        {
          id: 'sanbe-katsukazan',
          x: 0.74,
          y: 0.5,
          title: '中国地方に二つしかない活火山',
          body: '気象庁によると、日本には111の活火山があります。そのうち中国地方に挙げられているのは、三瓶山と阿武火山群の二つだけです。',
          source: 'https://www.jma.go.jp/jma/kishou/know/kazan/rireki/rireki.html',
          mapQuery: '三瓶山 大田市',
        },
        {
          id: 'sanbe-maibotsurin',
          x: 0.22,
          y: 0.48,
          title: '噴火に埋められた縄文の森',
          body: '三瓶山の噴火で地中深くに埋まった縄文時代の木々が、悠久の時を超えて当時のままの姿を現しています。大田市はこれを日本遺産「石見の火山が伝える悠久の歴史」のひとつとして紹介しています。',
          source: 'https://www.iwami-kazan.jp/',
          mapQuery: '三瓶小豆原埋没林公園',
        },
      ],
    },
    {
      id: 'shinjiko',
      name: '宍道湖の夕日',
      area: '松江市・夕',
      image: '/assets/scenery/shimane/scenes/09_shinjiko.webp',
      hotspots: [
        {
          id: 'shinjiko-yuhi',
          x: 0.46,
          y: 0.44,
          title: '日本夕陽百選の湖',
          body: '宍道湖は日本夕陽百選にも選ばれており、夕日の時間になると湖岸は地元のカメラマンや観光客でいっぱいになります。湖に沈む夕景は茜色の光に包まれます。',
          source: 'https://www.kankou-matsue.jp/shinjiko',
          mapQuery: '宍道湖夕日スポット 松江市',
        },
        {
          id: 'shinjiko-kisui',
          x: 0.74,
          y: 0.52,
          title: '海水と真水が混じる汽水湖',
          body: '宍道湖は全国で7番目に大きな汽水湖です。ここでとれる大粒のヤマトシジミは宍道湖七珍のひとつに数えられ、国内トップレベルの漁獲量を誇ります。出雲地方では、このシジミを使ったしじみ汁が日常の食卓に根づいています。',
          source:
            'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/shijimijiru_shimane.html',
          mapQuery: '宍道湖 しじみ漁',
        },
        {
          id: 'shinjiko-yuhishisu',
          x: 0.22,
          y: 0.4,
          title: '夕日が見えるかを数字にしている',
          body: '松江観光協会は夕日指数を公開しています。夕日が見える時間帯の雲の量や、夕日そのものが見える時間数から算出され、100から80ならはっきりと夕日が見られそう、20から0なら夕日を見るのは難しそう、と四段階で示されます。',
          source: 'https://www.kankou-matsue.jp/omoshiro/sunset/sunset_information',
          mapQuery: '宍道湖 嫁ヶ島',
        },
      ],
    },
    {
      id: 'hinomisaki',
      name: '出雲日御碕灯台',
      area: '出雲市・日没後',
      image: '/assets/scenery/shimane/scenes/10_hinomisaki.webp',
      hotspots: [
        {
          id: 'hinomisaki-1903',
          x: 0.46,
          y: 0.42,
          title: '明治36年からの灯台',
          body: '出雲日御碕灯台は明治36年に設置されました。海面から灯塔の灯火までは63.30メートル、地面から塔頂までの塔高は43.65メートルあります。',
          source: 'https://www.izumo-kankou.gr.jp/special/677',
          mapQuery: '出雲日御碕灯台',
        },
        {
          id: 'hinomisaki-163dan',
          x: 0.72,
          y: 0.52,
          title: '163段のらせん階段を上る',
          body: '灯台の内部には163段のらせん階段があり、展望台まで上ることができます。参観には寄付金が必要です。開いている時間があるので、出かける前に最新の発表を確かめてください。',
          source: 'https://www.izumo-kankou.gr.jp/special/677',
          mapQuery: '日御碕 出雲市',
        },
        {
          id: 'hinomisaki-juyobunkazai',
          x: 0.22,
          y: 0.48,
          title: '国の重要文化財',
          body: '1998年に世界の歴史的灯台100選、2013年に国の登録有形文化財、2022年に国指定重要文化財となっています。',
          source: 'https://www.izumo-kankou.gr.jp/special/677',
          mapQuery: '日御碕灯台 重要文化財',
        },
      ],
    },
  ],
};
